import React, { useState, PureComponent } from "react";
import "./filters.css";
import { Card, Col, Form, Alert, Spinner, Button, Row } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { IoMdClose } from "react-icons/io";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiAnalyse } from "react-icons/bi";
import { industriesNew } from "../../dropdown-options/industries";
import { countries } from "../../dropdown-options/countries";
import { entityTypes } from "../../dropdown-options/entity-types";
import { reasonsForShutdown } from "../../dropdown-options/reasons-for-shutdown";
import { lifespans } from "../../dropdown-options/lifespans";
import { fundingRounds } from "../../dropdown-options/funding-rounds";
import { genders } from "../../dropdown-options/genders";
import { noOfFounders } from "../../dropdown-options/no-of-founders";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * CustomizedAxisTick Component
 * Custom chart axis tick label renderer for Recharts bar charts with label rotation
 */
class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, stroke, payload } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="end" transform="rotate(-20)">
          {payload.value}
        </text>
      </g>
    );
  }
}

/**
 * FundingAmounts Filter Component
 * 
 * Analyzes startup funding distribution across investment amount brackets.
 * Shows frequency and percentage of startups receiving funding in each amount range.
 * Includes comprehensive filters for entity type, shutdown reasons, lifespan, location, industry, and founder demographics.
 * 
 * @param {Object} props - Component props including Supabase client
 * @param {Object} props.supabase - Supabase client instance
 * @returns {JSX.Element} Funding amount distribution chart
 */
const FundingAmounts = (props) => {
  // State to control alert visibility
  const [alert, setAlert] = useState(false);

  // State to store alert message content
  const [message, setMessage] = useState("");

  // Close alert and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // Get current user and Supabase client from context
  const { currentUser, supabase } = useSupabaseApp();

  // Check if user is authenticated; redirect if not
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // State to manage filter form inputs
  const [filters, setFilters] = useState({
    type: "Funding amount",
    entityType: "Entity type",
    reasons: "Reasons for shutdown",
    lifespan: "Lifespan",
    location: "Location",
    industry: "Industry",
    fundingRound: "Funding round",
    gender: "Gender",
    numOfFounders: "No. of founders",
  });

  // State to store chart data - startups by funding amount bracket
  const [investments, setInvestments] = useState([
    {
      "Total Investment Amount": "Less than $1M",
      Frequency: 3258,
      "%": 45.9,
    },
    {
      "Total Investment Amount": "$1M-$5M",
      Frequency: 1598,
      "%": 22.5,
    },
    {
      "Total Investment Amount": "$5M-$10M",
      Frequency: 685,
      "%": 9.6,
    },
    {
      "Total Investment Amount": "$10M-$50M",
      Frequency: 1188,
      "%": 16.7,
    },
    {
      "Total Investment Amount": "$50M-$100M",
      Frequency: 215,
      "%": 3.0,
    },
    {
      "Total Investment Amount": "$100M-$500M",
      Frequency: 145,
      "%": 2.0,
    },
    {
      "Total Investment Amount": "$500M-$1B",
      Frequency: 8,
      "%": 0.1,
    },
    {
      "Total Investment Amount": "$1B-$5B",
      Frequency: 5,
      "%": 0.1,
    },
    {
      "Total Investment Amount": "More than $5B",
      Frequency: 1,
      "%": 0,
    },
  ]);

  // Reset all filter form inputs to their default values and close any alert
  function resetFilters(e) {
    e.preventDefault();
    setFilters({
      type: "Funding amount",
      entityType: "Entity type",
      reasons: "Reasons for shutdown",
      lifespan: "Lifespan",
      location: "Location",
      industry: "Industry",
      fundingRound: "Funding round",
      gender: "Gender",
      numOfFounders: "No. of founders",
    });
    setAlert(false);
  }

  // Update filter state whenever user changes a form input value
  function changeFilters(e) {
    e.preventDefault();

    let name = e.target.name;
    let value = e.target.value;

    // Create a copy of current filters object
    let filtersCopy = { ...filters };

    // Update the changed filter property
    filtersCopy[name] = value;
    setFilters({ ...filtersCopy });
  }

  // Submit filters to Supabase edge function and fetch funding amount distribution data
  function submitFilters(e) {
    // Show loading alert while fetching data
    setAlert(true);
    setMessage(
      <div style={{ display: "flex", paddingTop: "10px" }}>
        <div style={{ width: "90%" }}>
          <p style={{ color: "#0f9d58" }}>
            <BiAnalyse className="alert-icon" /> Loading
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            Won't be long now. We're always doing our best to reduce waiting
            time.
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            If your waiting time is too long,{" "}
            <a
              href="https://www.roiquant.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>
            .
          </p>
        </div>
        <div style={{ width: "10%", textAlign: "right" }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </div>
    );

    supabase.functions
      .invoke("postmortemAnalysis", { body: filters })
      .then((response) => {
        let graphData = response.data;
        setInvestments(graphData);
        closeAlert();
      })
      .catch((error) => {
        setAlert(true);
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              Your filter query returned no results. Please change your filter
              values and try again.
            </p>
          </div>
        );
      });
    e.preventDefault();
  }

  return (
    <div className="accordion-card-body">
      {/* loading alert */}

      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      <div className="col-12 div-filter">
        {/* form */}
        <Form onSubmit={submitFilters}>
          <Form.Group className="inputgroup">
            <Row className="align-items-center">
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.entityType}
                  disabled={filters.filtersView}
                  name="entityType"
                  onChange={changeFilters}
                >
                  <option>Entity type</option>
                  {entityTypes.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.reasons}
                  name="reason"
                  onChange={changeFilters}
                >
                  <option>Reasons for shutdown</option>
                  {reasonsForShutdown.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.lifespan}
                  name="lifespan"
                  onChange={changeFilters}
                >
                  <option>Lifespan</option>
                  {lifespans.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.location}
                  name="location"
                  onChange={changeFilters}
                >
                  <option>Location</option>
                  {countries.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.industry}
                  name="industry"
                  onChange={changeFilters}
                >
                  <option>Industry</option>
                  {industriesNew.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.fundingRound}
                  name="fundingRound"
                  onChange={changeFilters}
                >
                  <option>Funding round</option>
                  {fundingRounds.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.gender}
                  name="gender"
                  onChange={changeFilters}
                >
                  <option>Gender</option>
                  {genders.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.numOfFounders}
                  name="numOfFounders"
                  onChange={changeFilters}
                >
                  <option>No. of founders</option>
                  {noOfFounders.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              {/* reset button */}
              <Col xs="auto">
                <button
                  onClick={resetFilters}
                  className="reset-button tour-clear-filter"
                >
                  <span>Clear</span>
                </button>
              </Col>
              {/* submit button */}
              <Col xs="auto">
                <button type="submit" className="submit-button">
                  <span>Filter</span>
                </button>
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </div>

      {/* results */}
      <div className="col-12">
        <Card className="graph-card">
          <div>
            {/* chart component */}
            <ResponsiveContainer id="vc-charts">
              <BarChart
                data={investments}
                margin={{ top: 20, right: 20, left: 20, bottom: 100 }}
                width={1100}
                height={500}
                fontFamily="AileronRegular"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="Total Investment Amount"
                  interval={0}
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  tick={<CustomizedAxisTick />}
                  label={{
                    value: "Funding Amounts",
                    offset: -80,
                    position: "insideBottom",
                  }}
                />
                <YAxis
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  label={{
                    value: "Frequency",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip />
                <Bar dataKey="Frequency" fill="#8884d8" barSize={50}>
                  <LabelList dataKey="%" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* notes */}
      <div className="col-12 extra-notes">
        <span className="info-notes">
          Note:
          <ul>
            <li id="notes-points">
              Based on{" "}
              {props.companiesWithFundingAmounts
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies with recorded{" "}
              <span style={{ fontFamily: "AileronItalic" }}>
                funding amount data
              </span>
              .
            </li>
            <li id="notes-points">
              In due time, the remaining{" "}
              {props.companiesWithFundingAmounts
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies without funding amount data will be identified
              and recorded by our algorithms.
            </li>
          </ul>
        </span>
      </div>
    </div>
  );
};

export default FundingAmounts;
